/**
 * An address this app may turn into a link someone else typed. Held to the backend's
 * `CallLinks.Accept`: `https://` (any case), at most 2048 characters, no whitespace, control,
 * invisible-format character or backslash, no credentials, a host. Pure.
 */

export interface HttpsLink {
  /** The parsed, normalised address — what an anchor's `href` carries. */
  readonly href: string;
  /** What the reader is shown of where the link goes; an international host stays in punycode. */
  readonly host: string;
}

export const HTTPS_LINK_MAX_LENGTH = 2048;

// The browser's parser forgives `https:host`, `https:\\host` and strips zero-width or bidi marks;
// the server refuses all of them, and a bidi mark can make the shown address lie.
const REFUSED_CHARACTERS = /[\s\\\p{Cc}\p{Cf}]/u;

const IPV4_PART = '(25[0-5]|2[0-4]\\d|1\\d\\d|[1-9]?\\d)';
const CANONICAL_IPV4 = new RegExp(`^${IPV4_PART}(\\.${IPV4_PART}){3}$`);

/**
 * The host as typed has no empty label, and a numeric or hex last label only in a canonical dotted
 * IPv4 — the parser rewrites `127.1` or `0x7f.1` into an address the reader never saw (mobile's rule).
 */
function hostAsTyped(value: string): boolean {
  const authority = value.slice('https://'.length).split(/[/?#]/, 1)[0] ?? '';
  if (authority.startsWith('[')) return true;
  const host = authority.replace(/:\d*$/, '').toLowerCase();
  const labels = host.split('.');
  if (labels.some((label) => label === '')) return false;
  const last = labels[labels.length - 1] ?? '';
  return !/^(\d+|0x[0-9a-f]*)$/.test(last) || CANONICAL_IPV4.test(host);
}

export function httpsLinkOf(value: string): HttpsLink | null {
  // The authority right after `//`: `https:///path` would otherwise parse with `path` as its host.
  if (!/^https:\/\/[^/?#]/i.test(value) || value.length > HTTPS_LINK_MAX_LENGTH) return null;
  if (REFUSED_CHARACTERS.test(value) || !hostAsTyped(value)) return null;
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }
  if (url.protocol !== 'https:' || url.hostname === '') return null;
  // `https://bank.pl@evil.example` reads as the bank and goes elsewhere.
  if (url.username !== '' || url.password !== '') return null;
  return { href: url.href, host: url.hostname };
}

/** The host as a sentence prints it: `www.` adds nothing the reader needs. */
export function shownHost(host: string): string {
  return host.replace(/^www\./, '');
}
